import React from "react";
import styled, { FlattenSimpleInterpolation } from "styled-components";
import colors from "../../../../styles/colors";
import { SortAscendingIcon, SortDescendingIcon } from "../../../pages/Analytics/AnalyticsIcons";
import { CustomCheckBox } from "../../../pages/Analytics/UsersTablePage/UsersPage.styles";

export interface IColumn<T> {
  name: string;
  accessor: keyof T;
  styles?: FlattenSimpleInterpolation;
}

export type OrderingDirection = "asc" | "desc";
export type OrderingColumn = "checked" | "learnerName" | "state" | "dateLastSeen" | "resets" | "time" | "topics";

export interface ITableProps<T> {
  columns: Array<IColumn<T>>;
  data: T[];
  emptyDataMessage?: string;
  sortingFns?: Array<() => void>;
  isFilteredOutRow?: boolean[];
  ordering?: [OrderingColumn, OrderingDirection];
  allSelected?: boolean;
}

interface TableHeaderProps<T> {
  idx: number;
  column: IColumn<T>;
}

interface SortingTableHeaderProps<T> extends TableHeaderProps<T> {
  sortingFn: () => void;
  ordering: [OrderingColumn, OrderingDirection];
  allSelected: boolean;
}

interface TableRowProps<T> {
  idx: number;
  item: T;
  columns: Array<IColumn<T>>;
}

interface FilteredTableRowProps<T> extends TableRowProps<T> {
  isFilteredOutRow: boolean;
}

const TableContainer = styled.table(
  ({ theme }) => `
  border-spacing: 0px;
  box-shadow: 0.5rem 0.5rem 4rem -1.5rem rgba(0, 0, 0, 0.5);
  width: 100%;
  thead {
    color: ${theme.colors.white};
    background-color: ${theme.colors.redTableHeader};
    font-size: ${theme.fontSizes.small};
    text-align: left;

    tr {
      height: 6.5rem;
    }
  }

  tr:first-child {
    background-color: ${theme.colors.headerAccent};
    color: ${theme.colors.tableHeadersTextColor};
  }

  th:last-child {
    border-radius: 0 1rem 0 0;
  }

  th:first-child {
    border-radius: 1rem 0 0 0;
  }

  th,
  td {
    padding: 0 2rem;
  }

  tr {
    height: 4.5rem;
  }

  tbody {
    tr:nth-child(odd) {
      background-color: white;
    }

    tr:nth-child(even) {
      background-color: ${theme.colors.lightgrey};
    }
  }
`
);

const Tr = styled.tr`
  td {
    color: ${(props: { filteredOut: boolean }) => (props.filteredOut ? colors.lightgrey : colors.black)};
  }
`;

const Th = styled.th<{ customStyle?: FlattenSimpleInterpolation }>`
  ${props => props.customStyle && props.customStyle}
  cursor: ${props => (props.onClick ? "pointer" : "undefined")};
`;

const Td = styled.td<{ customStyle?: FlattenSimpleInterpolation }>`
  ${props => props.customStyle && props.customStyle}
  color: ${({ theme }) => theme.colors.black};
`;

const SortingIconSpan = styled.span`
  position: relative;
  top: -2px;
  left: 0.5rem;
`;

const TableHeader = <T extends object>({ idx, column }: TableHeaderProps<T>) => {
  return (
    <Th key={idx} customStyle={column.styles} data-testid="default-th">
      {column.name}
    </Th>
  );
};

const SortingTableHeader = <T extends object>({
  idx,
  column,
  sortingFn,
  ordering,
  allSelected,
}: SortingTableHeaderProps<T>) => {
  return (
    <Th key={idx} customStyle={column.styles} onClick={sortingFn} data-testid="sorting-th">
      {column.accessor === "checked" && (
        <CustomCheckBox type="checkbox" onClick={sortingFn} checked={allSelected} data-testid="all-users-checkbox" />
      )}
      {column.name}
      {column.accessor === ordering[0] && (
        <SortingIconSpan>{ordering[1] === "asc" ? <SortAscendingIcon /> : <SortDescendingIcon />}</SortingIconSpan>
      )}
    </Th>
  );
};

const TableRow = <T extends object>({ idx, item, columns }: TableRowProps<T>) => {
  return (
    <tr key={idx} data-testid="default-tr">
      {columns.map((column, cellIdx) => (
        <Td key={cellIdx} customStyle={column.styles}>
          {item[column.accessor]}
        </Td>
      ))}
    </tr>
  );
};

const FilteredTableRow = <T extends object>({ idx, item, isFilteredOutRow, columns }: FilteredTableRowProps<T>) => {
  return (
    <Tr key={idx} filteredOut={isFilteredOutRow} data-testid="filtered-tr">
      {columns.map((column, cellIdx) => (
        <Td key={cellIdx} customStyle={column.styles}>
          {item[column.accessor]}
        </Td>
      ))}
    </Tr>
  );
};

export const Table = <T extends object>({
  columns,
  data,
  emptyDataMessage,
  sortingFns,
  isFilteredOutRow,
  ordering,
  allSelected,
}: ITableProps<T>) => {
  return (
    <TableContainer>
      <thead>
        <tr>
          {columns.map((column, idx) =>
            sortingFns && ordering && allSelected !== undefined ? (
              <SortingTableHeader
                idx={idx}
                column={column}
                sortingFn={sortingFns[idx]}
                ordering={ordering}
                allSelected={allSelected}
              />
            ) : (
              <TableHeader idx={idx} column={column} data-testid="default-th" />
            )
          )}
        </tr>
      </thead>
      <tbody>
        {data.length ? (
          data.map((item, idx) =>
            isFilteredOutRow ? (
              <FilteredTableRow
                idx={idx}
                item={item}
                isFilteredOutRow={isFilteredOutRow[idx]}
                columns={columns}
                data-testid="filtering-tr"
              />
            ) : (
              <TableRow idx={idx} item={item} columns={columns} data-testid="default-tr" />
            )
          )
        ) : (
          <tr>
            <Td colSpan={columns.length}>{emptyDataMessage ?? "No data"}</Td>
          </tr>
        )}
      </tbody>
    </TableContainer>
  );
};
